import React, { useState } from 'react'
import useScreenChecker from 'src/hooks/checkers/useScreenChecker'
import { useLocationChecker } from 'src/hooks/checkers/useLocationChecker'
import { RenderModules } from 'src/utils/renderModules'
import { ForceSelect } from 'src/components/ForceSelect'

export interface ConditionalProps {
  data: {
    name: string
    // Exactly one checker must be defined.
    checker: any[]
    // Only used when the checker is a screen checker.
    metModules?: []
    unmetModules?: []
  }
}

/**
 * Centralizes the resolution of a checker.
 * If it's a screenchecker, return a boolean.
 * If it's a locationchecker, return the module(s) corresponding to the user's location.
 */

const useResolvedChecker = (checker: any): boolean | any => {
  if (checker._type === 'screenchecker') {
    return useScreenChecker(checker)
  } else if (checker._type === 'locationchecker') {
    return useLocationChecker(checker)
  }

  console.warn('Unknown checker type:', checker._type)

  return undefined
}

/**
 * Renders modules based on the type and result of the checker.
 * For screencheckers, renders metModules if true or unmetModules if false.
 * For locationcheckers, always renders the module(s) from the hook result.
 */

const renderCheckerModules = (
  checker: any,
  result: boolean | any,
  metModules?: [],
  unmetModules?: []
) => {
  if (checker._type === 'screenchecker') {
    return result
      ? RenderModules(metModules || [])
      : RenderModules(unmetModules || [])
  } else if (checker._type === 'locationchecker') {
    // For location checker, we're assuming the result always includes a valid module
    // (with the fallback "Not In Market" always defined in the schema).
    return RenderModules(result)
  }

  return null
}

const Conditional = ({ data }: ConditionalProps) => {
  const [forceState, setForceState] = useState('no-force')
  const checker = data.checker[0]
  const resolved = useResolvedChecker(checker)
  const content = renderCheckerModules(
    checker,
    resolved,
    data.metModules,
    data.unmetModules
  )

  const shouldShowToggle =
    (process.env.GATSBY_ENVIRONMENT === 'local' ||
      process.env.GATSBY_ENVIRONMENT === 'staging') &&
    checker._type !== 'locationchecker'

  return (
    <>
      {shouldShowToggle && (
        <ForceSelect value={forceState} onChange={setForceState} />
      )}

      {forceState === 'true' &&
        checker._type === 'screenchecker' &&
        RenderModules(data.metModules || [])}

      {forceState === 'false' &&
        checker._type === 'screenchecker' &&
        RenderModules(data.unmetModules || [])}

      {forceState === 'no-force' && content}
    </>
  )
}

export default Conditional
